import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuill } from 'react-quilljs'
import { useLocation, useParams } from 'react-router-dom'
import { Form, Input, notification, Select, Typography } from 'antd'
import axios from 'axios'
import clsx from 'clsx'
import Const from 'constants/constant'
import dayjs from 'dayjs'
import useGetAnnotationColor from 'hooks/useGetAnnotationColor'
import useMutation from 'hooks/useMutation'
import ViewerFilePreview from 'pages/building/buildingDetailViewMode/components/ViewerFilePreview'
import CustomBlotFormatter from 'quill/CustomBlotFormatter'
import { useRecoilState, useSetRecoilState } from 'recoil'
import {
  hasLinkErrorState,
  isRefetchingAnnotationsState,
  selectedPointDetail,
  setAnnotationModeState
} from 'store/buildingStore'
import useSWR from 'swr'
import {
  AnnotationPayload,
  AnnotationType,
  BuildingFile,
  CustomFolder,
  LinkAnnotation,
  SetAnnotation
} from 'types/building'
import { KrpanoInterface } from 'types/krpano'
import Resizer from 'utils/imageResizer'
import Krpano from 'utils/krpano'
import registerQuillImageHandler from 'utils/quillUtils'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'
import DropdownIconCustom from 'components/icons/DropdownIconCustom'
import LeftArrowIcon from 'components/icons/LeftArrowIcon'
import RightArrowIcon from 'components/icons/RightArrowIcon'

import BCPMemoSetting from '../BCPAnnotationModal/BCPMemoSetting'
import useBcpAnnotation from '../BCPAnnotationModal/hooks/useBcpAnnotation'

import BCPSetAnnotationCategoryDetail from './BCPSetAnnotationCategoryDetail'

export default function BCPModalAddSetAnnotation() {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const name = Form.useWatch('name', form)
  const { id, floorId } = useParams()
  const location = useLocation()
  const setHasLinkError = useSetRecoilState(hasLinkErrorState)

  const isBuildingMode = useMemo(
    () =>
      !location.pathname.startsWith('/tour') &&
      !location.pathname.startsWith('/bcp'),
    [location.pathname]
  )

  // quill for  申し送り
  const {
    quill: quillHandover,
    quillRef: quillRefHandover,
    Quill: QuillClassHandover
  } = useQuill({
    theme: 'snow',
    modules: Const.QUILL_MODULES,
    formats: Const.QUILL_FORMATS,
    placeholder: '入力してください'
  })

  const { hasLinkError } = useBcpAnnotation()
  const { getAnnotationBgColor } = useGetAnnotationColor()

  const [annotationType, setAnnotationType] = useState<AnnotationType>(
    AnnotationType.FIRE
  )
  const [nextAnnotationType, setNextAnnotationType] = useState<AnnotationType>()
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedTabs, setSelectedTabs] = useState<number>(1)
  const [nameParse, setNameParse] = useState<string[]>(['', '', ''])
  const [linkParse, setLinkParse] = useState<string[]>(['', '', ''])
  const [memoParse, setMemoParse] = useState<string[]>(['', '', ''])
  const [currentListFile, setCurrentListFile] = useState<BuildingFile[]>([])
  const [currentListLink, setCurrentListLink] = useState<
    (LinkAnnotation & { category?: number | string })[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingContent, setIsFetchingContent] = useState(false)
  const [handoverName, setHandoverName] = useState({
    handoverFileName: '',
    handoverOccurenceFileName: '',
    handoverRecoveryFileName: ''
  })
  const [hasHandoverError, setHasHandoverError] = useState(false)
  const [formHandover] = Form.useForm()

  const [previewFileIndex, setPreviewFileIndex] = useState<number | undefined>(
    undefined
  )
  const [existsFile, setExistsFile] = useState<BuildingFile[]>([])
  const prevHandoverTabRef = useRef<number | null>(null)
  const isInitialized = useRef<boolean>(false)

  const annotationBgColor = useMemo(
    () => getAnnotationBgColor(annotationType),
    [getAnnotationBgColor, annotationType]
  )

  const [{ selectedSetAnnotation, annotationId }, setSelectedMode] =
    useRecoilState(setAnnotationModeState)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [, setIsRefetchingAnnotations] = useRecoilState(
    isRefetchingAnnotationsState
  )

  useEffect(() => {
    isInitialized.current = false
  }, [selectedSetAnnotation])

  const { data: setAnnotationData, isValidating: isValidatingDetail } =
    useSWR<SetAnnotation>(
      selectedSetAnnotation
        ? {
            url: `v1/buildings/${id}/set-annotations/${selectedSetAnnotation}`
          }
        : null
    )

  const { data: bcpCustomFolders } = useSWR({
    url: `v1/bcp/buildings/${id}/folders`
  })

  const { trigger: triggerAddSetAnnotation, isMutating: isAddSetAnnotation } =
    useMutation(`v1/buildings/${id}/set-annotations`, { throwOnError: true })
  const { trigger: getPresignedUrl, isMutating: isGettingPresignedUrl } =
    useMutation('v1/annotations/presigned-urls')
  const { trigger: deleteOldContent, isMutating: isDeletingOldContent } =
    useMutation('v1/delete-file')

  const { trigger: getBuildingPresignedUrl } = useMutation(
    'v1/buildings/presigned-urls'
  )
  const { trigger: saveFileMetadata } = useMutation('v1/buildings/files')

  const [memoContent, setMemoContent] = useState('')
  const { mutate: refetchPointDetail } = useSWR(
    selectedSetAnnotation && pointDetail?.id
      ? {
          url: `v1/points/${pointDetail.id}`,
          params: {
            building_id: id,
            floor_id: floorId
          }
        }
      : null,
    {
      revalidateOnMount: false
    }
  )

  const annotationList = useMemo(
    () => [
      ...Const.BCP_ANNOTATION_LIST,
      ...(bcpCustomFolders?.map(
        (item: { categoryId: number; name: string }) => ({
          value: item.categoryId,
          label: item.name
        })
      ) || [])
    ],
    [bcpCustomFolders]
  )

  const listTabs = useMemo((): { value: number; label: string }[] => {
    const customChildrenFolders = bcpCustomFolders?.find(
      (item: CustomFolder) => item.categoryId === annotationType
    )
    if (
      Object.values(AnnotationType).includes(annotationType || '') ||
      !customChildrenFolders
    ) {
      return Const.BCP_ANNOTATION_TABS.map((item) => ({
        value: annotationType + item.value,
        label: item.label
      }))
    }
    return (
      customChildrenFolders?.childFolders?.map(
        (item: { categoryId: number; name: string }) => ({
          value: item.categoryId,
          label: item.name
        })
      ) || []
    )
  }, [annotationType, bcpCustomFolders])

  const selectedTabIndex = useMemo(
    () => listTabs.findIndex((item) => item.value === selectedTabs),
    [listTabs, selectedTabs]
  )

  const listMemos = useMemo(
    () => [
      setAnnotationData?.memo,
      setAnnotationData?.memo_occurence,
      setAnnotationData?.memo_recovery
    ],
    [
      setAnnotationData?.memo,
      setAnnotationData?.memo_occurence,
      setAnnotationData?.memo_recovery
    ]
  )

  const checkIsEmpty = useCallback(() => {
    const hasFiles = currentListFile.length > 0
    const hasLinks = currentListLink.some((l) => l.link?.trim())
    const hasMemo = memoParse.some((m) => !Krpano.IsMemoEmpty(m))
    const hasHandover =
      handoverName.handoverFileName?.trim() ||
      handoverName.handoverOccurenceFileName?.trim() ||
      handoverName.handoverRecoveryFileName?.trim()

    return !hasFiles && !hasLinks && !hasMemo && !hasHandover
  }, [currentListFile.length, currentListLink, memoParse, handoverName])

  const handleChangeType = (value: number) => {
    if (selectedSetAnnotation || !checkIsEmpty()) {
      setNextAnnotationType(value)
      return
    }
    setAnnotationType(value)
  }

  const getLinkContent = () => {
    if (selectedCategory === 'link') {
      const links = form.getFieldValue(['linksStage', selectedTabs])
      const firstLink =
        Array.isArray(links) && links.length > 0 ? links[0]?.link || '' : ''
      return linkParse.map((item: string, index: number) =>
        index === selectedTabIndex ? firstLink : item
      )
    }
    return linkParse
  }

  const getNameContent = () => {
    if (selectedCategory === 'link') {
      const links = form.getFieldValue(['linksStage', selectedTabs])
      const firstName =
        Array.isArray(links) && links.length > 0 ? links[0]?.name || '' : ''
      return nameParse.map((item: string, index: number) =>
        index === selectedTabIndex ? firstName : item
      )
    }
    return nameParse
  }

  const getMemoContent = (): string[] => {
    if (selectedCategory === 'handover') {
      return memoParse.map((item: string, index: number) =>
        index === selectedTabIndex ? quillHandover?.root.innerHTML || '' : item
      )
    }
    return memoParse
  }

  const handleFillParseValue = () => {
    setNameParse(getNameContent())
    setLinkParse(getLinkContent())
    setMemoParse(getMemoContent())
  }

  const handleCategoryBack = () => {
    handleFillParseValue()
    setSelectedCategory('')
  }

  const checkAllTabsError = async () => {
    try {
      await form.validateFields()
      setHasLinkError(false)
    } catch {
      setHasLinkError(true)
    }
  }

  const handleClickBack = () => {
    if (hasLinkError && selectedCategory === 'link') return
    handleCategoryBack()
  }

  const handleChangeTab = async (tab: number) => {
    if (selectedCategory === 'link') {
      setTimeout(() => {
        checkAllTabsError()
      }, 100)
    }
    handleFillParseValue()
    setSelectedTabs(tab)
  }

  const getHandoverNameField = useCallback((index: number): string => {
    switch (index) {
      case 0:
        return 'handoverFileName'
      case 1:
        return 'handoverOccurenceFileName'
      case 2:
        return 'handoverRecoveryFileName'
      default:
        return ''
    }
  }, [])

  const getHandoverNameValue = useCallback(
    (index: number): string => {
      switch (index) {
        case 0:
          return handoverName.handoverFileName || ''
        case 1:
          return handoverName.handoverOccurenceFileName || ''
        case 2:
          return handoverName.handoverRecoveryFileName || ''
        default:
          return ''
      }
    },
    [handoverName]
  )

  const validateHandover = useCallback(
    (overrideName?: string): boolean => {
      const field = getHandoverNameField(selectedTabIndex)
      const nameContent = overrideName ?? formHandover.getFieldValue(field)
      const isNameEmpty = !nameContent?.trim()
      const isNameValid =
        nameContent?.trim()?.length >= 1 && nameContent?.trim()?.length <= 100
      const html = quillHandover?.root?.innerHTML || ''
      const htmlEmpty = Krpano.IsMemoEmpty(html)
      const plainText = quillHandover?.getText?.() || ''
      const textEmpty = plainText.replace(/\s|\n/g, '').length === 0
      const isContentEmpty = htmlEmpty && textEmpty
      const hasError = isNameEmpty || !isNameValid || isContentEmpty
      setHasHandoverError(hasError)
      return !hasError
    },
    [formHandover, getHandoverNameField, quillHandover, selectedTabIndex]
  )

  const handleHandoverNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      const field = getHandoverNameField(selectedTabIndex)
      setHandoverName((prev) => ({ ...prev, [field]: value }))
      formHandover.setFieldsValue({ [field]: value })
      validateHandover(value)
    },
    [getHandoverNameField, selectedTabIndex, formHandover, validateHandover]
  )

  const handleCloseModal = () => {
    if (selectedSetAnnotation) {
      if (annotationId) {
        setSelectedMode({
          selectedSetAnnotation: undefined,
          setAnnotationDetail: undefined,
          annotationId: undefined,
          mode: undefined
        })
        return
      }
      setSelectedMode((prev) => ({
        ...prev,
        setAnnotationDetail: undefined,
        mode: 'listSetAnnotation'
      }))
      return
    }
    setSelectedMode((prev) => ({
      ...prev,
      mode: undefined,
      selectedSetAnnotation: undefined
    }))
  }

  const editSelectedSetAnnotation = () => {
    if (selectedSetAnnotation) {
      const krpano = document.getElementById(
        'embedpano-full'
      ) as unknown as KrpanoInterface
      pointDetail?.annotations.forEach((item) => {
        if (item.set_annotation_id === selectedSetAnnotation) {
          switch (annotationType) {
            case 50:
              krpano?.call(`load_style(${item.id}, 'fire')`)
              break
            case 60:
              krpano?.call(`load_style(${item.id}, 'flood')`)
              break
            case 70:
              krpano?.call(`load_style(${item.id}, 'earthquake')`)
              break
            case 80:
              krpano?.call(`load_style(${item.id}, 'volcano')`)
              break
            default:
              krpano?.call(`load_style(${item.id}, 'tradition')`)
              break
          }
        }
      })
      setPointDetail(
        (prev) =>
          prev && {
            ...prev,
            annotations: prev?.annotations.map((item) =>
              item.set_annotation_id === selectedSetAnnotation
                ? { ...item, type: annotationType }
                : item
            )
          }
      )
    }
  }

  const hanldeUploadMemo = async () => {
    if (annotationType === AnnotationType.MEMO) {
      if (Krpano.IsMemoEmpty(memoContent)) return [null, null, null]
      const newQuillInnerHtml = await Resizer.ResizedQuillImages(memoContent)
      const file = new Blob([newQuillInnerHtml], { type: 'text/plain' })
      if (file.size / (1024 * 1024) > 50) {
        notification.error({
          key: 'memoTooLarge',
          message: t('common.validationError.fileTooLarge')
        })
        return [null, null, null]
      }
      const data = await getPresignedUrl({
        data: { extension: 'txt', fileName: 'annotation-content' }
      })
      const { key = undefined, presignedUrl = undefined } = {
        ...data?.data?.s3PreSignedLinks[0]
      }
      await axios.put(presignedUrl, file)
      return [key, null, null]
    }
    const promises = getMemoContent().map(async (item) => {
      if (item) {
        const isMemoEmpty = Krpano.IsMemoEmpty(item)
        if (isMemoEmpty) return null
        const newQuillInnerHtml = await Resizer.ResizedQuillImages(item)
        const file = new Blob([newQuillInnerHtml], {
          type: 'text/plain'
        })
        if (file.size / (1024 * 1024) > 50) {
          notification.error({
            key: 'memoTooLarge',
            message: t('common.validationError.fileTooLarge')
          })
          return null
        }
        const data = await getPresignedUrl({
          data: {
            extension: 'txt',
            fileName: 'annotation-content'
          }
        })
        const { key = undefined, presignedUrl = undefined } = {
          ...data?.data?.s3PreSignedLinks[0]
        }
        await axios.put(presignedUrl, file)
        return new Promise((resolve) => {
          resolve(key)
        })
      }
      return null
    })
    const results: (string | null | unknown)[] = await Promise.all(promises)
    return results as (string | null)[]
  }

  const handleAddSetAnnotation = async () => {
    if (
      isAddSetAnnotation ||
      isGettingPresignedUrl ||
      isDeletingOldContent ||
      isFetchingContent
    )
      return

    try {
      if (selectedCategory === 'link') {
        await form.validateFields()
      }
      setIsLoading(true)
      const isHandoverType = (type: number | string | undefined) => {
        return [
          AnnotationType.FIRE,
          AnnotationType.FLOOD,
          AnnotationType.EARTHQUAKE,
          AnnotationType.VOLCANO,
          AnnotationType.LIFE_LINE
        ].includes(Number(type))
      }

      if (isHandoverType(annotationType)) {
        const pairs = [
          [getMemoContent()[0], handoverName.handoverFileName],
          [getMemoContent()[1], handoverName.handoverOccurenceFileName],
          [getMemoContent()[2], handoverName.handoverRecoveryFileName]
        ]

        const isEmptyHtml = (html: string) => {
          return Krpano.IsMemoEmpty(html)
        }

        const errorPairs: number[] = []

        pairs.forEach(([html, text], index) => {
          const htmlEmpty = isEmptyHtml(html)
          const textEmpty = !text || text.trim() === ''

          if (!htmlEmpty && textEmpty) {
            errorPairs.push(index)
          }

          if (htmlEmpty && !textEmpty) {
            errorPairs.push(index)
          }
        })

        const isValid = errorPairs.length === 0

        if (!isValid) {
          const errorTabs = errorPairs.map((i) => t(listTabs[i].label))

          const errorMessage = errorTabs.join(', ')

          notification.error({
            message: t('common.validationError.memoContentRequired', {
              tab: errorMessage
            })
          })

          setIsLoading(false)
          return
        }
      }

      if (selectedSetAnnotation) {
        listMemos.forEach(async (item) => {
          if (item) {
            await deleteOldContent({
              data: { key: Krpano.GetMemoKeyFromPresignedUrl(item) }
            })
          }
        })
      }

      if (annotationType === AnnotationType.MEMO) {
        const isMemoEmpty = Krpano.IsMemoEmpty(memoContent || '')
        if (isMemoEmpty) {
          notification.error({
            message: t('building.validationError.memoContentRequired')
          })
          setIsLoading(false)
          return
        }
      }

      const uploadedKeys = await hanldeUploadMemo()

      let finalFiles: BuildingFile[] = currentListFile

      if (currentListFile) {
        const pendingFiles = currentListFile.filter((f) => f.rawFile)
        if (pendingFiles.length > 0) {
          finalFiles = await Promise.all(
            currentListFile.map(async (fileItem) => {
              if (!fileItem.rawFile) return fileItem

              const file = fileItem.rawFile
              const category = fileItem.category || 0

              // 0. Extract width/height for images natively before upload
              let width: number | undefined
              let height: number | undefined

              if (file.type.startsWith('image/')) {
                const img = new Image()
                img.src = URL.createObjectURL(file)
                await new Promise((resolve) => {
                  img.onload = () => {
                    width = img.width
                    height = img.height
                    URL.revokeObjectURL(img.src)
                    resolve(true)
                  }
                  img.onerror = () => {
                    URL.revokeObjectURL(img.src)
                    resolve(true)
                  }
                })
              }

              // 1. Get Presigned URL
              const presignedData = await getBuildingPresignedUrl({
                data: {
                  buildingId: Number(id),
                  count: 1,
                  category
                }
              })

              const { presignedUrl, key: s3Key } =
                presignedData.data.s3PreSignLinks[0]

              // 2. Upload to S3
              await axios.put(presignedUrl, file)

              // 3. Save to Metadata API
              const savedFileData = await saveFileMetadata({
                data: {
                  buildingId: Number(id),
                  category,
                  extension: fileItem.name.split('.').pop(),
                  name: fileItem.name,
                  mimeType: file.type,
                  size: file.size,
                  s3Path: s3Key,
                  extraInfo:
                    width && height ? JSON.stringify({ width, height }) : null,
                  takenAt: dayjs(file.lastModified).format(
                    'YYYY-MM-DDTHH:mm:ssZ'
                  ),
                  overwriteID: fileItem.overwriteID,
                  mode: fileItem.mode
                }
              })

              const resData = savedFileData.data
              return {
                id: resData.id,
                name: resData.name || file.name,
                s3Path: resData.s3_path || resData.s3Path || s3Key,
                mimeType: resData.mime_type || resData.mimeType || file.type,
                extension:
                  resData.extension ||
                  file.name.split('.').pop()?.toLowerCase() ||
                  '',
                createdAt:
                  resData.created_at ||
                  resData.createdAt ||
                  new Date().toISOString(),
                size: resData.size || file.size,
                thumbnail:
                  resData.thumbnail ||
                  resData.s3_path ||
                  resData.s3Path ||
                  s3Key,
                category,
                pointId: resData.point_id || resData.pointId || null,
                takenAt: resData.taken_at || resData.takenAt || null
              }
            })
          )
        }
      }

      if (currentListFile) {
        const renamedFiles = currentListFile.filter(
          (f) => f.id > 0 && f.originalName && f.originalName !== f.name
        )
        if (renamedFiles.length > 0) {
          await Promise.all(
            renamedFiles.map((fileItem) =>
              saveFileMetadata({
                method: 'patch',
                query: [fileItem.id.toString()],
                data: { name: fileItem.name }
              })
            )
          )
        }
      }

      const allStageLinks: (LinkAnnotation & { category: number | string })[] =
        []
      listTabs.forEach((tab) => {
        const stageLinks = form.getFieldValue(['linksStage', tab.value])
        if (stageLinks && Array.isArray(stageLinks)) {
          allStageLinks.push(
            ...stageLinks.filter(Boolean).map((l: LinkAnnotation) => ({
              link: l.link || '',
              name: l.name || '',
              category: tab.value
            }))
          )
        }
      })

      const getStageLinks = (idx: number) => {
        const tab = listTabs[idx]
        return allStageLinks.filter((l) => l.category === tab?.value)
      }

      const prepLinks = getStageLinks(0)
      const occuLinks = getStageLinks(1)
      const recoLinks = getStageLinks(2)

      const payload: Partial<AnnotationPayload> = {
        links:
          prepLinks.length > 0
            ? prepLinks.map((l: { link?: string; name?: string }) => ({
                link: (l.link || '').trim(),
                name: (l.name || '').trim()
              }))
            : [{ link: '', name: '' }],
        link_occurrences:
          occuLinks.length > 0
            ? occuLinks.map((l: { link?: string; name?: string }) => ({
                link_occurrence: (l.link || '').trim(),
                name_occurrence: (l.name || '').trim()
              }))
            : [{ link_occurrence: '', name_occurrence: '' }],
        link_recoveries:
          recoLinks.length > 0
            ? recoLinks.map((l: { link?: string; name?: string }) => ({
                link_recovery: (l.link || '').trim(),
                name_recovery: (l.name || '').trim()
              }))
            : [{ link_recovery: '', name_recovery: '' }],
        name,
        // Set singular fields to undefined to prioritize plural arrays
        link: undefined,
        name_occurrence: undefined,
        link_occurrence: undefined,
        name_recovery: undefined,
        link_recovery: undefined,

        memo: uploadedKeys[0] || '',
        memo_occurence: uploadedKeys[1] || '',
        memo_recovery: uploadedKeys[2] || '',

        memo_file_name: handoverName.handoverFileName,
        memo_occurence_file_name: handoverName.handoverOccurenceFileName,
        memo_recovery_file_name: handoverName.handoverRecoveryFileName,

        type: annotationType,
        files: finalFiles.map((item) => item.id)
      }

      await triggerAddSetAnnotation({
        method: selectedSetAnnotation ? 'patch' : 'post',
        data: {
          ...payload,
          pointID: pointDetail?.id,
          annotationID: annotationId,
          floorID: Number(floorId),
          pointOrder: selectedSetAnnotation ? Number(pointDetail?.order) : ''
        },
        query: selectedSetAnnotation ? [selectedSetAnnotation] : []
      })
      setIsLoading(false)
      notification.success({
        message: t('building.saved')
      })
      editSelectedSetAnnotation()
      setIsRefetchingAnnotations(true)
      handleCloseModal()
      refetchPointDetail()
    } catch (error) {
      setIsLoading(false)
    }
  }

  const checkSubmitDisabled = useCallback(() => {
    const isMemoType = annotationType === AnnotationType.MEMO
    const isMemoInvalid =
      isMemoType &&
      (!name ||
        name?.trim()?.length === 0 ||
        Krpano.IsMemoEmpty(memoContent || ''))

    const isHandoverInvalid =
      selectedCategory === 'handover' &&
      (hasHandoverError ||
        (!handoverName.handoverFileName?.trim() &&
          !handoverName.handoverOccurenceFileName?.trim() &&
          !handoverName.handoverRecoveryFileName?.trim()))
    const isLinkInvalid = selectedCategory === 'link' && hasLinkError

    return isMemoInvalid || isHandoverInvalid || isLinkInvalid
  }, [
    annotationType,
    name,
    memoContent,
    selectedCategory,
    hasHandoverError,
    hasLinkError,
    handoverName.handoverFileName,
    handoverName.handoverOccurenceFileName,
    handoverName.handoverRecoveryFileName
  ])

  const handleSubmit = async () => {
    try {
      if (annotationType === AnnotationType.MEMO) {
        if (!name?.trim()) {
          notification.error({ message: 'メモ名を入力してください' })
          return
        }
      } else if (selectedCategory === 'link') {
        await form.validateFields()
      } else if (selectedCategory === 'handover') {
        await formHandover.validateFields()
      }

      await handleAddSetAnnotation()
    } catch (error: unknown) {
      const err = error as { errorFields?: unknown[] }
      if (err?.errorFields) {
        console.error(error)
      }
    }
  }

  const handleConfirmChangeType = () => {
    if (nextAnnotationType) {
      setAnnotationType(nextAnnotationType)
      setLinkParse(['', '', ''])
      setNameParse(['', '', ''])
      setMemoParse(['', '', ''])
      setCurrentListFile([])
      setCurrentListLink([]) // thêm dòng này
      setNextAnnotationType(undefined)
    }
  }

  const handleFetchContent = useCallback(async () => {
    setIsFetchingContent(true)
    const newMemos = listMemos.map(async (item) => {
      if (item) {
        const textContent = await Krpano.FetchMemoContent(item)
        return textContent
      }
      return ''
    })
    const results = await Promise.all(newMemos)
    setMemoParse(results)
    setIsFetchingContent(false)
    // custom placeholder of popup link
    const tooltipInput = document.querySelector('.ql-tooltip input[data-link]')
    if (tooltipInput) {
      tooltipInput.setAttribute('data-link', 'https://')
      tooltipInput.setAttribute('placeholder', 'https://')
    }
  }, [listMemos])

  useEffect(() => {
    handleFetchContent()
  }, [handleFetchContent])

  useEffect(() => {
    if (
      !isValidatingDetail &&
      selectedSetAnnotation &&
      setAnnotationData &&
      !isInitialized.current
    ) {
      const firstLink = setAnnotationData.links?.[0]
      const firstOccu = setAnnotationData.link_occurrences?.[0]
      const firstReco = setAnnotationData.link_recoveries?.[0]

      setLinkParse([
        firstLink?.link || '',
        firstOccu?.link_occurrence || setAnnotationData?.link_occurrence || '',
        firstReco?.link_recovery || setAnnotationData?.link_recovery || ''
      ])
      setNameParse([
        firstLink?.name || '',
        firstOccu?.name_occurrence ||
          setAnnotationData?.link_name_occurrence ||
          '',
        firstReco?.name_recovery || setAnnotationData?.link_name_recovery || ''
      ])
      setAnnotationType(setAnnotationData.type)
      setCurrentListFile(setAnnotationData?.files || [])
      form.setFieldValue('name', setAnnotationData.name)
      setSelectedMode((prev) => ({
        ...prev,
        setAnnotationDetail: setAnnotationData
      }))
      setHandoverName({
        handoverFileName: setAnnotationData.memo_file_name || '',
        handoverOccurenceFileName:
          setAnnotationData.memo_occurence_file_name || '',
        handoverRecoveryFileName:
          setAnnotationData.memo_recovery_file_name || ''
      })
      let occuLinks: (LinkAnnotation & { category?: number | string })[] = []
      if (
        setAnnotationData?.link_occurrences &&
        setAnnotationData.link_occurrences.length > 0
      ) {
        occuLinks = setAnnotationData.link_occurrences.map(
          (l: { link_occurrence?: string; name_occurrence?: string }) => ({
            link: l.link_occurrence,
            name: l.name_occurrence,
            category: listTabs[1]?.value
          })
        )
      } else if (setAnnotationData?.link_occurrence) {
        occuLinks = [
          {
            link: setAnnotationData.link_occurrence,
            name: setAnnotationData.link_name_occurrence || '',
            category: listTabs[1]?.value
          }
        ]
      }

      let recoLinks: (LinkAnnotation & { category?: number | string })[] = []
      if (
        setAnnotationData?.link_recoveries &&
        setAnnotationData.link_recoveries.length > 0
      ) {
        recoLinks = setAnnotationData.link_recoveries.map(
          (l: { link_recovery?: string; name_recovery?: string }) => ({
            link: l.link_recovery,
            name: l.name_recovery,
            category: listTabs[2]?.value
          })
        )
      } else if (setAnnotationData?.link_recovery) {
        recoLinks = [
          {
            link: setAnnotationData.link_recovery,
            name: setAnnotationData.link_name_recovery || '',
            category: listTabs[2]?.value
          }
        ]
      }

      const normalizedLinks: (LinkAnnotation & {
        category?: number | string
      })[] = [
        ...(setAnnotationData?.links || []).map((l: LinkAnnotation) => ({
          link: l.link,
          name: l.name,
          category: listTabs[0]?.value
        })),
        ...occuLinks,
        ...recoLinks
      ]
      setCurrentListLink(normalizedLinks)
      isInitialized.current = true
    }
  }, [
    form,
    isValidatingDetail,
    selectedSetAnnotation,
    setAnnotationData,
    setSelectedMode,
    listTabs
  ])

  useEffect(() => {
    if (listTabs) {
      setSelectedTabs(listTabs[0].value)
    }
  }, [listTabs])
  //

  useEffect(() => {
    if (QuillClassHandover && !quillHandover) {
      if (!QuillClassHandover.imports['modules/blotFormatter']) {
        QuillClassHandover.register(
          'modules/blotFormatter',
          CustomBlotFormatter
        )
      }
    }
  }, [QuillClassHandover, quillHandover])

  useEffect(() => {
    registerQuillImageHandler(quillHandover, t)
  }, [quillHandover, t])

  // effect fetch content for handover memo and overwrite placeholder
  useEffect(() => {
    if (selectedCategory === 'handover' && quillHandover) {
      const currentContent = quillHandover.root.innerHTML || ''
      const desiredContent = memoParse[selectedTabIndex] || ''
      const tabChanged = prevHandoverTabRef.current !== selectedTabIndex
      if (
        tabChanged ||
        (Krpano.IsMemoEmpty(currentContent) &&
          currentContent !== desiredContent &&
          currentContent !== '<p><br></p>')
      ) {
        quillHandover.root.innerHTML = desiredContent
      }
      prevHandoverTabRef.current = selectedTabIndex
      const field = getHandoverNameField(selectedTabIndex)
      formHandover.setFieldsValue({
        [field]: getHandoverNameValue(selectedTabIndex)
      })
      validateHandover()
      setTimeout(() => {
        let tooltipInput = null
        if (quillHandover && quillHandover.container) {
          if (quillHandover.container.parentNode) {
            tooltipInput = quillHandover.container.parentNode.querySelector(
              '.ql-tooltip input[data-link]'
            )
          }
          if (!tooltipInput) {
            tooltipInput = quillHandover.container.querySelector(
              '.ql-tooltip input[data-link]'
            )
          }
        }
        if (tooltipInput) {
          tooltipInput.setAttribute('data-link', 'https://')
          tooltipInput.setAttribute('placeholder', 'https://')
        }
      }, 100)
    }
  }, [
    memoParse,
    quillHandover,
    selectedCategory,
    selectedTabIndex,
    getHandoverNameField,
    getHandoverNameValue,
    formHandover,
    validateHandover
  ])

  // revalidate on Quill changes
  useEffect(() => {
    if (!quillHandover || selectedCategory !== 'handover') return
    const handler = () => {
      requestAnimationFrame(() => validateHandover())
    }
    quillHandover.on('text-change', handler)
    quillHandover.on('selection-change', handler)
    return () => {
      quillHandover.off('text-change', handler)
      quillHandover.off('selection-change', handler)
    }
  }, [quillHandover, selectedCategory, validateHandover])

  // unmount all quill when modal closed
  useEffect(() => {
    return () => {
      if (quillHandover) {
        quillHandover.disable()
        quillHandover.root.innerHTML = ''
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Modal
        open
        width={1200}
        className="paddingless-modal"
        destroyOnClose
        onCancel={handleCloseModal}
      >
        <div className="px-[60px] py-10 flex flex-col items-center">
          <Title height={28.96} title={t('building.addSetAnnotation')} />
          <Form
            form={form}
            labelCol={{ span: 5 }}
            labelAlign="left"
            wrapperCol={{ span: 19 }}
            colon={false}
            className="w-full flex flex-col gap-[30px]"
            onFieldsChange={(_, allFields) => {
              const hasError = allFields.some(
                (f) =>
                  f.name &&
                  Array.isArray(f.name) &&
                  f.name[0] === 'linksStage' &&
                  f.errors &&
                  f.errors.length > 0
              )
              setHasLinkError(hasError)
            }}
          >
            <div className="flex gap-5 items-center">
              <Typography className="font-bold text-[14px] leading-[14px] text-[#021120] w-[140px] whitespace-nowrap flex gap-2 items-center">
                {t('building.setAnnotationName')}
                <span className="text-red-500 text-xs whitespace-nowrap">
                  ※必須
                </span>
              </Typography>
              <Form.Item
                name="name"
                className="mb-0 thin-error-message"
                rules={[
                  {
                    validator: Validators.keywordsAndLengthCustomValidator(
                      t,
                      100,
                      true,
                      true
                    )
                  }
                ]}
              >
                <Input
                  className="w-60 h-[38px]"
                  placeholder="入力してください"
                />
              </Form.Item>
            </div>
            <div className="flex gap-5 items-center">
              <Typography className="font-bold text-[14px] leading-[14px] text-[#021120] w-[140px]">
                {t('building.annotationType')}
              </Typography>
              <Select
                value={annotationType}
                onSelect={(value) => handleChangeType(value)}
                options={annotationList}
                suffixIcon={<DropdownIconCustom colorIcon="#000000" />}
                style={{
                  width: 240,
                  fontSize: 14,
                  color: '#021120'
                }}
              />
            </div>

            <div className="relative" style={{ minHeight: 400 }}>
              <div
                className={
                  annotationType === AnnotationType.MEMO
                    ? 'visible'
                    : 'invisible'
                }
                style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
              >
                <BCPMemoSetting
                  memo={setAnnotationData?.memo}
                  onMemoContentChange={setMemoContent}
                />
              </div>
              <div
                className={
                  annotationType === AnnotationType.MEMO
                    ? 'invisible'
                    : 'visible'
                }
                style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
              >
                <div
                  className={`flex items-center h-[50px] ${annotationBgColor}`}
                >
                  {selectedCategory ? (
                    <div
                      className={clsx(
                        'h-6 ml-5',
                        hasLinkError && selectedCategory === 'link'
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer'
                      )}
                      onClick={handleClickBack}
                    >
                      <LeftArrowIcon size={24} />
                    </div>
                  ) : null}
                  <div className="flex relative">
                    {listTabs.map((item) => (
                      <div
                        key={item.value}
                        className="flex w-20 h-[50px] cursor-pointer"
                        onClick={() => handleChangeTab(item.value)}
                      >
                        <div className="text-base text-white m-auto">
                          {t(item.label)}
                        </div>
                      </div>
                    ))}
                    <div
                      className="absolute bottom-0 transition-all w-20 h-[5px] bg-[#A1C6B4]"
                      style={{
                        left: `${selectedTabIndex * 80}px`
                      }}
                    />
                  </div>
                </div>
                <div className="relative">
                  <div
                    className={clsx(selectedCategory ? 'hidden z-0' : 'z-10')}
                  >
                    {Const.BCP_ANNOTATION_TAB_CATEGORIES.map((item) => (
                      <div
                        key={item.value}
                        className="flex items-center justify-between h-[60px] cursor-pointer border-0 border-b border-solid border-[#EEEFF2] !px-5"
                        onClick={() => setSelectedCategory(item.value)}
                      >
                        <Typography className="text-[#021120] text-base leading-[23.17px] font-medium">
                          {t(item.label)}
                        </Typography>
                        <RightArrowIcon color="#3B3C46" size={24} />
                      </div>
                    ))}
                  </div>

                  <div
                    className={clsx(
                      selectedCategory ? 'opacity-100' : 'opacity-0 hidden'
                    )}
                  >
                    {selectedCategory !== 'handover' && (
                      <BCPSetAnnotationCategoryDetail
                        form={form}
                        selectedType={annotationType}
                        selectedTabIndex={selectedTabIndex}
                        selectedTabs={selectedTabs}
                        selectedCategory={selectedCategory}
                        linkParse={linkParse}
                        nameParse={nameParse}
                        currentListFile={currentListFile}
                        currentListLink={currentListLink}
                        setCurrentListFile={setCurrentListFile}
                        setExistsFile={setExistsFile}
                        setPreviewFileIndex={setPreviewFileIndex}
                        isValidatingDetail={isValidatingDetail}
                      />
                    )}

                    <div
                      className={`absolute top-0 left-0 w-full h-full ${
                        selectedCategory === 'handover'
                          ? 'opacity-100 z-[9999]'
                          : 'opacity-0 z-[-1]'
                      }`}
                    >
                      <Form component={false} form={formHandover}>
                        <div className="pt-3 ml-14">
                          <Typography className="text-[#021120] text-[14px] leading-[1]">
                            申し送り名
                          </Typography>
                          <Form.Item
                            name={getHandoverNameField(selectedTabIndex)}
                            className="!mb-0 flex-1 !w-1/2 !min-h-[72px] pt-2"
                            rules={[
                              {
                                required: true,
                                message: '申し送り名を入力してください'
                              },
                              {
                                min: 1,
                                max: 100,
                                message: '1〜100文字で入力してください'
                              }
                            ]}
                          >
                            <Input
                              placeholder="入力してください"
                              value={getHandoverNameValue(selectedTabIndex)}
                              onChange={handleHandoverNameChange}
                              className="w-full"
                            />
                          </Form.Item>

                          <div className="h-[300px] px-[0px]">
                            <div
                              ref={quillRefHandover}
                              className="quill-custom-editor overflow-hidden [&>#editor-resizer>.toolbar]:!hidden [&>.ql-editor]:pb-10"
                            />
                          </div>
                        </div>
                      </Form>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={clsx(
                'flex w-full items-end pt-5 pr-[30px] mt-24 gap-10 border-0 border-t border-solid border-[#EEEFF2]',
                selectedCategory !== 'handover' ? 'pt-5' : 'pt-6'
              )}
            >
              {selectedSetAnnotation && (
                <Button
                  sizing="w-[140px] h-[50px]"
                  className="bg-[#B80808]"
                  onClick={() =>
                    setSelectedMode((prev) => ({
                      ...prev,
                      mode: 'deleteSetAnnotation'
                    }))
                  }
                >
                  {t('translation.delete')}
                </Button>
              )}
              <div className="flex gap-10 ml-auto">
                <Button sizing="w-[140px] h-[50px]" onClick={handleCloseModal}>
                  {t('translation.cancel')}
                </Button>

                <Button
                  sizing="w-[140px] h-[50px]"
                  className="theme-bg-primary text-white"
                  style={{
                    color:
                      (isFetchingContent || checkSubmitDisabled()) &&
                      isBuildingMode
                        ? '#A0A0A0'
                        : 'white'
                  }}
                  loading={isLoading || isFetchingContent}
                  onClick={handleSubmit}
                  disabled={isFetchingContent || checkSubmitDisabled()}
                >
                  {t('building.buttonSave')}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </Modal>

      {nextAnnotationType ? (
        <Modal
          open
          width={580}
          rootClassName="shared-permission-modal"
          onCancel={() => setNextAnnotationType(undefined)}
        >
          <div className="flex flex-col gap-10 text-center p-5">
            <Typography className="text-[14px] leading-[21px]">
              {t('building.confirmChangeAnnotation')}
            </Typography>

            <div className="flex gap-5 mt-5">
              <Button
                sizing="w-[140px] h-[50px] mx-auto"
                onClick={() => setNextAnnotationType(undefined)}
              >
                {t('building.buttonCancel')}
              </Button>
              <Button
                sizing="w-[140px] h-[50px] mx-auto"
                onClick={handleConfirmChangeType}
                className="theme-bg-primary"
                type="primary"
              >
                {t('building.buttonSave')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {previewFileIndex !== undefined && (
        <ViewerFilePreview
          fileIndex={previewFileIndex}
          file={existsFile[previewFileIndex]}
          maxLength={existsFile.length}
          setPreviewFileIndex={setPreviewFileIndex}
        />
      )}
    </>
  )
}
